/**
 * La barra dei moduli: icona **e** nome, sempre. Attiva: striscia magenta.
 *
 * Il nome compariva al passaggio del mouse. Chi lavora su uno schermo tattile
 * non ha quel passaggio, e chi non riconosce un glifo non ci passa sopra per
 * scoprirlo: guarda cinque segni e non sa dove andare. Sui lettori di schermo
 * il nome c'era gia' — cioe' chi non vede si orientava meglio di chi vede, che
 * descrive bene il difetto.
 */
import { Link, NavLink } from 'react-router-dom'

import type { Whoami } from '../auth'
import { defaultPathFor } from '../home'
import { useT } from '../i18n/LocaleProvider'
import { LanguageSwitch } from './LanguageSwitch'
import { Mark } from './Mark'
import { isAiutoScreen } from '../aiuto/AiutoTabs'
import { railItems, type RailItem } from './railTargets'

function Icon({ id }: { id: string }): JSX.Element {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    'aria-hidden': true as const,
  }
  if (id === 'upload') {
    return (
      <svg {...common}>
        <path d="M8 12V4M4.5 7.5 8 4l3.5 3.5" />
        <path d="M3 13h10" />
      </svg>
    )
  }
  if (id === 'deliveries') {
    return (
      <svg {...common}>
        <path d="M2.5 6.5 8 3.5l5.5 3v6L8 15.5l-5.5-3z" />
        <path d="M2.5 6.5 8 9.5l5.5-3M8 9.5v6" />
      </svg>
    )
  }
  if (id === 'operations') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="5" />
        <path d="M8 5v3l2 1.5M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15" />
      </svg>
    )
  }
  if (id === 'admin') {
    return (
      <svg {...common}>
        <circle cx="8" cy="5.5" r="2.2" />
        <path d="M3.5 13c.6-2.2 2.3-3.5 4.5-3.5s3.9 1.3 4.5 3.5" />
      </svg>
    )
  }
  if (id === 'help') {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="6" />
        <path d="M6.3 6.3a1.7 1.7 0 013.4 0c0 1-1.7 1.3-1.7 2.4M8 11.3h.01" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <rect x="2.5" y="3" width="11" height="10" rx="1" />
      <path d="M5 6.5h6M5 9h4" />
    </svg>
  )
}

export interface ModuleRailProps {
  who: Whoami
  pathname: string
  /**
   * Quante commesse aspettano verifica, in tutto. Serve a vedere il carico
   * senza aprire lo schermo — e a vedere che non ce n'e', il che vale
   * altrettanto: `null` e zero si mostrano nello stesso modo, cioe' niente.
   * Un «0» appeso all'icona sembra un guasto piu' che una buona notizia.
   */
  waiting?: number | null
  /**
   * Chiude la sessione. La persona sta in fondo alla barra (ADR-009): il
   * disco con le iniziali apre nome, lingua, cambio password e uscita. Senza
   * `onLeave` il disco non c'e' — la barra si monta anche dove la sessione
   * non e' sua da chiudere.
   */
  onLeave?: () => void
}

/**
 * Le iniziali per il disco: prime lettere delle prime due parole del nome
 * mostrato; se il nome e' una parola sola, le prime due lettere del login.
 */
export function initialsOf(who: Pick<Whoami, 'display_name' | 'login'>): string {
  const words = who.display_name.trim().split(/\s+/).filter((one) => one.length > 0)
  const [first, second] = words
  if (first !== undefined && second !== undefined) return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase()
  return who.login.slice(0, 2).toUpperCase()
}

export function ModuleRail({ who, pathname, waiting = null, onLeave }: ModuleRailProps): JSX.Element {
  const items = railItems(who)
  const t = useT()
  return (
    <nav className="module-rail" aria-label={t('rail.modules')}>
      <Link to={defaultPathFor(who) ?? '/'} className="rail-marchio" aria-label="GridFly">
        <Mark size={30} fixed />
      </Link>
      {items.map((item) => {
        const label = t(item.label)
        const count = item.id === 'work' && waiting !== null && waiting > 0 ? waiting : null
        return (
          <NavLink
            key={item.id}
            to={item.to}
            className={() => (railActive(item, pathname) ? 'rail-item on' : 'rail-item')}
            // Il conteggio entra nel nome accessibile: chi non vede la pastiglia
            // deve comunque sapere che il modulo ha del lavoro dentro.
            aria-label={count === null ? label : t('rail.workWaiting', { n: count })}
          >
            <Icon id={item.id} />
            {/* Nascosta ai lettori di schermo perche' `aria-label` sul
                collegamento dice gia' la stessa cosa, col conteggio in piu'. */}
            <span className="rail-etichetta" aria-hidden="true">
              {label}
            </span>
            {count !== null && (
              <span className="rail-conto" aria-hidden="true">
                {count}
              </span>
            )}
          </NavLink>
        )
      })}
      <span className="rail-spazio" aria-hidden="true" />
      <NavLink
        to="/aiuto"
        className={() => (isAiutoScreen(pathname) ? 'rail-item rail-aiuto on' : 'rail-item rail-aiuto')}
        aria-label={t('rail.help')}
      >
        <Icon id="help" />
        <span className="rail-etichetta" aria-hidden="true">
          {t('rail.help')}
        </span>
      </NavLink>
      {onLeave !== undefined && (
        <details className="rail-persona">
          <summary className="rail-avatar" aria-label={t('chrome.identity')} title={who.display_name}>
            {initialsOf(who)}
          </summary>
          <div data-theme="cockpit" className="rail-persona-menu" role="group" aria-label={t('chrome.identity')}>
            <p className="rail-persona-nome">
              {who.display_name} <span className="shell-login tenue">{who.login}</span>
            </p>
            <LanguageSwitch />
            <Link to="/password/change" className="rail-persona-voce">
              {t('title.password')}
            </Link>
            <button type="button" onClick={onLeave} className="rail-persona-voce">
              {t('chrome.signOut')}
            </button>
          </div>
        </details>
      )}
    </nav>
  )
}

export function railActive(item: RailItem, pathname: string): boolean {
  if (item.id === 'admin') return pathname.startsWith('/admin') || pathname.startsWith('/operations')
  if (item.id === 'upload') return pathname.startsWith('/uploads')
  if (item.id === 'deliveries') return pathname.startsWith('/exports')
  if (item.id === 'work') {
    return pathname === '/work' || pathname.startsWith('/inspections') || pathname.startsWith('/ingest')
  }
  return pathname === item.to
}
